import { motion } from "framer-motion";

export const STAR_PATH =
  "M0,-1 C0.12,-0.32 0.32,-0.12 1,0 C0.32,0.12 0.12,0.32 0,1 C-0.12,0.32 -0.32,0.12 -1,0 C-0.32,-0.12 -0.12,-0.32 0,-1 Z";

export const Star = ({ x, y, s, fill = "#FFF3D6", opacity = 1, glow = false, twinkle = false }) => (
  <path
    d={STAR_PATH}
    transform={`translate(${x} ${y}) scale(${s})`}
    fill={fill}
    opacity={opacity}
    className={twinkle ? "twinkle" : undefined}
    style={glow ? { filter: "drop-shadow(0 0 16px rgba(255,224,150,0.95))" } : undefined}
  />
);

const ChromeDefs = () => (
  <defs>
    <radialGradient id="ms-chrome" cx="35%" cy="30%" r="85%">
      <stop offset="0%" stopColor="#ffffff" />
      <stop offset="35%" stopColor="#d7dae3" />
      <stop offset="70%" stopColor="#7c8291" />
      <stop offset="100%" stopColor="#22252d" />
    </radialGradient>
  </defs>
);

const Sphere = ({ cx, cy, r }) => (
  <g>
    <circle cx={cx} cy={cy} r={r} fill="url(#ms-chrome)" />
    <ellipse cx={cx - r * 0.3} cy={cy - r * 0.38} rx={r * 0.3} ry={r * 0.18} fill="rgba(255,255,255,0.95)" />
  </g>
);

export const CompassFace = ({ showText = true, selected = null, className = "" }) => (
  <svg viewBox="0 0 640 640" className={`overflow-visible ${className}`}>
    <ChromeDefs />
    {/* crosshair */}
    <line x1="320" y1="46" x2="320" y2="594" stroke="#FFF3D6" strokeOpacity="0.35" strokeDasharray="2 9" />
    <line x1="46" y1="320" x2="594" y2="320" stroke="#FFF3D6" strokeOpacity="0.35" strokeDasharray="2 9" />
    {/* tilted orbits */}
    <ellipse cx="320" cy="320" rx="294" ry="118" fill="none" stroke="#F6E7C8" strokeOpacity="0.55" strokeDasharray="10 9" transform="rotate(-16 320 320)" />
    <ellipse cx="320" cy="320" rx="294" ry="202" fill="none" stroke="#F6E7C8" strokeOpacity="0.32" strokeDasharray="4 10" transform="rotate(-16 320 320)" />
    {/* rings */}
    <circle cx="320" cy="320" r="252" fill="none" stroke="#F6E7C8" strokeOpacity="0.7" strokeDasharray="7 11" />
    <circle cx="320" cy="320" r="206" fill="none" stroke="#F6E7C8" strokeOpacity="0.5" strokeDasharray="3 8" />
    <circle cx="320" cy="320" r="150" fill="none" stroke="#F6E7C8" strokeOpacity="0.4" />
    {/* intercardinal stars on the r206 ring */}
    <Star x="465.7" y="174.3" s="9" twinkle />
    <Star x="465.7" y="465.7" s="9" />
    <Star x="174.3" y="465.7" s="9" twinkle />
    <Star x="174.3" y="174.3" s="9" />
    {/* tiny outer stars */}
    <Star x="538" y="194" s="6" opacity="0.85" twinkle />
    <Star x="102" y="194" s="6" opacity="0.85" />
    <Star x="102" y="446" s="6" opacity="0.85" twinkle />
    <Star x="538" y="446" s="6" opacity="0.85" />
    {/* cardinal stars */}
    <Star x="320" y="66" s="24" glow={selected === "N"} />
    <Star x="574" y="320" s="24" glow={selected === "E"} />
    <Star x="320" y="574" s="24" glow={selected === "S"} />
    <Star x="66" y="320" s="24" glow={selected === "W"} />
    {/* chrome spheres */}
    <Sphere cx="320" cy="12" r="12" />
    <Sphere cx="628" cy="320" r="12" />
    <Sphere cx="320" cy="628" r="12" />
    <Sphere cx="12" cy="320" r="12" />
    <Sphere cx="468" cy="88" r="5" />
    <Sphere cx="556" cy="172" r="4" />
    <Sphere cx="172" cy="84" r="4" />
    <Sphere cx="88" cy="172" r="5" />
    <Sphere cx="556" cy="468" r="5" />
    <Sphere cx="468" cy="556" r="4" />
    <Sphere cx="84" cy="468" r="4" />
    <Sphere cx="172" cy="556" r="5" />
    {showText && (
        <g transform="rotate(-4 320 320)">
        <text
          x="320"
          y="322"
          textAnchor="middle"
          fill="#FFF6E3"
          style={{ fontFamily: "Hurricane, cursive", fontSize: "96px" }}
        >
          Mar Star
        </text>
        <text
          x="320"
          y="392"
          textAnchor="middle"
          fill="#FFE9C9"
          style={{ fontFamily: "'Space Mono', monospace", fontSize: "30px", letterSpacing: "10px" }}
        >
          ORIGINALS
        </text>
      </g>
    )}
  </svg>
);

const BUTTON_POS = {
  N: "left-1/2 top-[10.3%] -translate-x-1/2 -translate-y-1/2",
  E: "left-[89.7%] top-1/2 -translate-x-1/2 -translate-y-1/2",
  S: "left-1/2 top-[89.7%] -translate-x-1/2 -translate-y-1/2",
  W: "left-[10.3%] top-1/2 -translate-x-1/2 -translate-y-1/2",
};

const DIRS = [
  { id: "N", label: "North", angle: 0 },
  { id: "E", label: "East", angle: 90 },
  { id: "S", label: "South", angle: 180 },
  { id: "W", label: "West", angle: 270 },
];

export default function Compass({ selected, locked, onSelect }) {
  const target = DIRS.find((d) => d.id === selected);
  return (
    <div className="relative aspect-square w-[min(86vw,540px)]" data-testid="compass">
      <div className="aura-strong absolute -inset-[16%] rounded-full" />
      <motion.div
        className="relative h-full w-full"
        animate={{ rotate: locked && target ? target.angle + 720 : 0 }}
        transition={{ duration: 1.5, ease: [0.65, 0, 0.2, 1] }}
      >
        <CompassFace showText={false} selected={selected} className="h-full w-full" />
      </motion.div>
      <img
        src="/assets/logo-mark.png"
        alt="MarStar Originals"
        data-testid="compass-logo"
        className="pointer-events-none absolute left-1/2 top-1/2 w-[58%] -translate-x-1/2 -translate-y-1/2 -rotate-3"
        style={{ filter: "drop-shadow(0 4px 30px rgba(43,47,217,0.45))" }}
      />
      {DIRS.map((d) => (
        <button
          key={d.id}
          type="button"
          data-testid={`compass-direction-${d.id.toLowerCase()}`}
          aria-label={`Choose ${d.label}`}
          disabled={locked}
          onClick={() => onSelect(d.id)}
          className={`group absolute z-10 flex h-16 w-16 items-center justify-center rounded-full transition-transform duration-300 ${
            BUTTON_POS[d.id]
          } ${locked && selected !== d.id ? "opacity-30" : ""} ${
            !locked ? "cursor-pointer hover:scale-125" : "cursor-default"
          }`}
        >
          <span
            className={`font-mono text-xs font-bold tracking-[0.2em] transition-colors ${
              selected === d.id ? "text-ink" : "text-ink/70 group-hover:text-ink"
            }`}
          >
            {d.id}
          </span>
        </button>
      ))}
    </div>
  );
}
